import React, { useEffect, useRef } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { Button } from '../UI-CHAKRA/Button';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import Badge from '../UI-CHAKRA/Badge';
import { FreeSampleCatalogBrand, FreeSampleMedicine } from '../../interfaces/freeSample';
import FreeSampleHeader from './FreeSampleHeader';

interface FreeSampleProductsStepProps {
  brand: FreeSampleCatalogBrand;
  selectedSkus: string[];
  onToggleSku: (sku: string) => void;
  onSelectInterest: (item: FreeSampleMedicine) => void;
  onContinue: () => void;
  onBack: () => void;
  onClose: () => void;
  onEventEmission: (eventName: string, extraParams?: Record<string, unknown>) => void;
}

const ApprovalBadge: React.FC = () => (
  <Badge
    backgroundColor={'#F1F3F5'}
    color={'#495057'}
    fontWeight={500}
    fontSize={'12px'}
    textTransform={'none'}
    borderRadius={'6px'}
    padding={'2px 8px'}
  >
    Sujeito à aprovação de laboratório
  </Badge>
);

// Painel de detalhe de uma marca. Bifurca em 2 modos conforme a elegibilidade do médico pra essa
// marca (Figma, nó 5047-78): elegível -> checklist multi-seleção de SKUs (vira items[] com vários
// SKUs da marca numa única solicitação); não elegível -> cada item tem uma ação "Tenho interesse"
// avulsa, que leva ao fluxo de interesse (item único, sem SKU — vira ELIGIBILITY_INCLUSION_REQUEST).
const FreeSampleProductsStep: React.FC<FreeSampleProductsStepProps> = ({
  brand,
  selectedSkus,
  onToggleSku,
  onSelectInterest,
  onContinue,
  onBack,
  onClose,
  onEventEmission,
}) => {
  const isEligible = brand.eligible;
  const hasEmittedViewEventRef = useRef(false);

  const getSelectedEans = () => brand.items.filter((item) => selectedSkus.includes(item.sku)).map((item) => item.eanCode);

  useEffect(() => {
    if (hasEmittedViewEventRef.current) return;
    hasEmittedViewEventRef.current = true;
    onEventEmission('backoffice_campaign_amostra_gratis_product_view', {
      laboratory: brand.brand,
      idMedicamentoEan: getSelectedEans(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emitProductClick = (continued: boolean) => {
    onEventEmission('backoffice_campaign_amostra_gratis_product_click', {
      laboratory: brand.brand,
      selectedProducts: selectedSkus,
      idMedicamentoEan: getSelectedEans(),
      continouSolicitacao: continued,
    });
  };

  const handleContinue = () => {
    emitProductClick(true);
    onContinue();
  };

  const handleBack = () => {
    emitProductClick(false);
    onBack();
  };

  const handleClose = () => {
    onEventEmission('backoffice_campaign_amostra_gratis_produtos_cancelar_click');
    onClose();
  };

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      <FreeSampleHeader onClose={handleClose} />

      <Box flexShrink={0} padding={'16px 20px 8px 20px'} borderBottom={'1px solid #E9ECEF'}>
        <Text fontSize={'16px'} fontWeight={700} color={'#343A40'}>
          {brand.brand}
        </Text>
        <Text fontSize={'13px'} color={'#6C757D'}>
          Todas as amostras estão sujeitas à aprovação do laboratório.
        </Text>
      </Box>

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden">
        {brand.items.length === 0 ? (
          <Flex justifyContent={'center'} padding={'40px'}>
            <Text fontSize={'14px'} color={'#6C757D'}>
              Nenhum produto disponível para esta marca.
            </Text>
          </Flex>
        ) : (
          brand.items.map((item) =>
            isEligible ? (
              <Flex key={item.sku} flexDirection={'row'} padding={'12px 20px'} borderBottom={'1px solid #E9ECEF'} gap={'12px'}>
                <Checkbox
                  isChecked={selectedSkus.includes(item.sku)}
                  onChange={() => onToggleSku(item.sku)}
                  mt={'2px'}
                />
                <Box flex={1} cursor={'pointer'} onClick={() => onToggleSku(item.sku)}>
                  <Text fontSize={'15px'} fontWeight={700} color={'#343A40'}>
                    {item.medicineName}
                  </Text>
                  <Box mt={'4px'}>
                    <ApprovalBadge />
                  </Box>
                </Box>
              </Flex>
            ) : (
              <Flex
                key={item.sku}
                flexDirection={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                padding={'12px 20px'}
                borderBottom={'1px solid #E9ECEF'}
                gap={'12px'}
              >
                <Box flex={1}>
                  <Text fontSize={'15px'} fontWeight={700} color={'#343A40'}>
                    {item.medicineName}
                  </Text>
                  <Box mt={'4px'}>
                    <ApprovalBadge />
                  </Box>
                </Box>
                <Text
                  fontSize={'13px'}
                  fontWeight={700}
                  color={'primary.main'}
                  cursor={'pointer'}
                  whiteSpace={'nowrap'}
                  onClick={() => onSelectInterest(item)}
                >
                  Tenho interesse
                </Text>
              </Flex>
            ),
          )
        )}
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={handleBack}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
          {isEligible && (
            <Button
              onClick={handleContinue}
              isDisabled={selectedSkus.length === 0}
              padding="10px 20px"
              backgroundColor={'primary.main'}
              borderRadius={'20px'}
              gap={'8px'}
              color={'#FFF'}
              fontSize={'14px'}
              _hover={{ backgroundColor: '#6B409C' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              rightIcon={<RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" />}
            >
              Continuar
            </Button>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

export default FreeSampleProductsStep;
